import { META_ROOT_ID } from "../constants.js";
/**
 * Minimal CSS reset for the shadow root.
 *
 * Two-layer reset strategy:
 *
 * 1. `:host { all: initial }` — resets inherited properties on the shadow host
 *    for outer rules that do NOT use `!important`. This covers the common case.
 *
 * 2. `* { color: initial; font-family: var(--font-sans) }` — explicitly sets
 *    inherited properties on shadow-internal elements to break the inheritance
 *    chain from the (potentially poisoned) host. `color: initial` clears any
 *    inherited color; `font-family: var(--font-sans)` uses the design token
 *    that pierces the shadow boundary rather than reverting to the browser's
 *    default serif. CSS selectors from the outer document cannot reach shadow-
 *    internal elements, so no `!important` is needed here.
 *    The `*` rule has specificity `(0,0,0)` so it is overridden by any
 *    Tailwind utility class `(0,1,0)` — @repo/ui components render correctly.
 *
 * CSS custom properties intentionally pierce the shadow boundary and are NOT
 * affected by `all: initial`, so design tokens remain available for Tailwind.
 */
const SHADOW_RESET_CSS = `
  :host {
    all: initial;
    display: block;
  }
  *, *::before, *::after {
    box-sizing: border-box;
    font-family: var(--font-sans);
  }
`;

/**
 * Mounts the meta layer inside the existing `<div id="$$meta-root">` element.
 *
 * Attaches an open shadow root and seeds it with a constructable stylesheet
 * that resets inherited CSS properties, giving the shadow a known baseline
 * independent of whatever styles the prototype document applies.
 *
 * Returns the ShadowRoot for the caller to mount UI into.
 */
export function mountMetaLayer(doc: Document): ShadowRoot {
  const el = doc.getElementById(META_ROOT_ID);
  if (!el) {
    throw new Error("[mountMetaLayer] #$$meta-root not found in document");
  }

  const shadow = el.attachShadow({ mode: "open" });

  const sheet = new CSSStyleSheet();
  sheet.replaceSync(SHADOW_RESET_CSS);
  shadow.adoptedStyleSheets = [sheet];

  return shadow;
}
